import React from "react";
import Skeleton from "../common/skeleton";
import SectionError from "./section_error";

const formatKrw = (n) => `₩${Math.round(Number(n) || 0).toLocaleString()}`;

const formatPct = (n) => {
    const v = Number(n) || 0;
    return `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
};

// 배분 스택 바의 모노크롬 램프 — silver 시스템에는 카테고리 색상이 없으므로
// content 토큰의 불투명도 단계(비중 순위별)로 구분한다. 5단계 이후는 마지막 단계 재사용.
const RAMP_STEPS = [88, 68, 50, 36, 24];

const rampColor = (rank) => {
    const pct = RAMP_STEPS[Math.min(rank, RAMP_STEPS.length - 1)];
    return `color-mix(in srgb, var(--color-content) ${pct}%, transparent)`;
};

/**
 * PortfolioAllocation — 포트폴리오 구성(배분) + 종목별 손익률 비교 카드.
 *
 * 상단: 비중(weight_pct) 스택 바 — 모노크롬 램프(비중 큰 순) + 범례 칩.
 * 하단: 종목별 손익률(pnl_pct) 다이버징 바 — 중앙 0 기준 좌(손실)/우(수익),
 *       손익률 내림차순 정렬, 스케일은 최대 |손익률| 기준.
 * 데이터는 HoldingsTable과 동일한 holdings 배열을 공유한다 (확정 체결 집계).
 */
function PortfolioAllocation({ holdings, loading, error, onRetry }) {
    if (loading) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
                <Skeleton variant="line" width="30%" height={14} />
                <Skeleton variant="rect" height={96} className="mt-3" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
                <SectionError message="포트폴리오 정보를 불러오지 못했습니다." onRetry={onRetry} />
            </div>
        );
    }

    const rows = Array.isArray(holdings) ? holdings : [];
    if (rows.length === 0) return null; // 보유 없음 — 페이지 빈 상태가 안내 담당

    // 비중 내림차순(배분 바·범례 순서), 손익률 내림차순(비교 바 순서)
    const byWeight = [...rows].sort((a, b) => (Number(b.weight_pct) || 0) - (Number(a.weight_pct) || 0));
    const byPnl = [...rows].sort((a, b) => (Number(b.pnl_pct) || 0) - (Number(a.pnl_pct) || 0));
    const maxAbsPnl = Math.max(...rows.map((h) => Math.abs(Number(h.pnl_pct) || 0)), 0.01);

    return (
        <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
            <div className="text-sm font-semibold text-content-secondary">포트폴리오</div>

            {/* 배분 스택 바 */}
            <div
                className="mt-3 flex h-3 w-full overflow-hidden rounded-full bg-surface-elevated"
                role="img"
                aria-label={`보유 비중: ${byWeight.map((h) => `${h.ticker} ${Math.round(Number(h.weight_pct) || 0)}%`).join(", ")}`}
            >
                {byWeight.map((h, i) => (
                    <div
                        key={h.ticker}
                        style={{
                            width: `${Math.max(Number(h.weight_pct) || 0, 0)}%`,
                            background: rampColor(i),
                        }}
                        className={i > 0 ? "border-l-2 border-surface-card" : ""}
                    />
                ))}
            </div>
            {/* 범례 칩 */}
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                {byWeight.map((h, i) => (
                    <span key={h.ticker} className="inline-flex items-center gap-1.5 text-xs text-content-secondary">
                        <span
                            aria-hidden="true"
                            className="inline-block h-2.5 w-2.5 rounded-sm"
                            style={{ background: rampColor(i) }}
                        />
                        <span className="font-num font-semibold text-content">{h.ticker}</span>
                        <span className="font-num">{(Number(h.weight_pct) || 0).toFixed(1)}%</span>
                    </span>
                ))}
            </div>

            {/* 종목별 손익률 다이버징 바 */}
            <div className="mt-4 border-t border-surface-border pt-3">
                <div className="text-xs font-semibold text-content-muted">종목별 손익률</div>
                <div className="mt-2 space-y-1.5">
                    {byPnl.map((h) => {
                        const pnl = Number(h.pnl_pct) || 0;
                        const positive = pnl >= 0;
                        const barPct = Math.min((Math.abs(pnl) / maxAbsPnl) * 100, 100);
                        return (
                            <div key={h.ticker} className="flex items-center gap-2">
                                <span className="w-14 shrink-0 font-num text-xs font-semibold text-content">
                                    {h.ticker}
                                </span>
                                <div
                                    className="relative h-2.5 min-w-0 flex-1 rounded-full bg-surface-elevated"
                                    role="img"
                                    aria-label={`${h.ticker} 손익률 ${formatPct(pnl)}`}
                                >
                                    {/* 중앙 0 기준선 */}
                                    <div className="absolute inset-y-0 left-1/2 w-px bg-surface-border-light" aria-hidden="true" />
                                    <div
                                        className={`absolute inset-y-0 rounded-full ${positive ? "bg-gain" : "bg-loss"}`}
                                        style={
                                            positive
                                                ? { left: "50%", width: `${barPct / 2}%` }
                                                : { right: "50%", width: `${barPct / 2}%` }
                                        }
                                        aria-hidden="true"
                                    />
                                </div>
                                <span
                                    className={`w-16 shrink-0 text-right font-num text-xs font-semibold ${positive ? "text-gain" : "text-loss"}`}
                                >
                                    {formatPct(pnl)}
                                </span>
                                <span className="hidden w-24 shrink-0 text-right font-num text-xs text-content-muted sm:block">
                                    {formatKrw(h.value_krw)}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default PortfolioAllocation;
